import React from 'react';
import { initializeApp, getApps } from 'firebase/app';
import { collection, getFirestore, onSnapshot, query, where } from 'firebase/firestore';

/**
 * The coffee menu, read live from the coffee boards on the wall at Project 9.
 *
 * Those boards are custom menus in Project 9's Firebase project, built on the
 * catalog's "Coffee" category. Their menu collections allow public reads, so
 * this opens a second, read-only, signed-out app on that project. Whatever the
 * bar changes on the boards (drinks, the featured roaster, the bags on the
 * shelf) shows up here with no separate update.
 */
const PROJECT9_FIREBASE = {
  apiKey: 'AIzaSyDUyoQLdo2u3_nS2aRqz4hdEYb69d2VZ34',
  authDomain: 'project9brewing.firebaseapp.com',
  projectId: 'project9brewing',
  storageBucket: 'project9brewing.appspot.com',
  messagingSenderId: '49403411075',
  appId: '1:49403411075:web:a456e56bd98423248cf9b6',
};
const APP_NAME = 'project9brewing';

const project9Db = () =>
  getFirestore(getApps().find((app) => app.name === APP_NAME) ?? initializeApp(PROJECT9_FIREBASE, APP_NAME));

/** The live Full Tank menu on the Project 9 site. */
export const LIVE_MENU_URL = 'https://www.project9brewing.com/menu?tab=coffee';
/** The taproom's own menu at Project 9: beer, wine and the rest. */
export const TAPROOM_MENU_URL = 'https://www.project9brewing.com/menu';

interface Category {
  id: string;
  name: string;
  subcategories?: { id: string; name: string }[];
}

interface BoardSection {
  id: string;
  name: string;
  sectionType: string;
  productSubcategoryId?: string;
  productSubcategoryIds?: string[];
  productIds?: string[];
  productIdsToHide?: string[];
  roasterName?: string;
  roasterLocation?: string;
  roasterBlurb?: string;
  roasterLogo?: string;
}

interface CustomMenu {
  id: string;
  name: string;
  productCategoryId?: string;
  columns: { sections: BoardSection[] }[];
}

export interface Item {
  id: string;
  name: string;
  description?: string;
  images?: string[];
  productSubcategoryId?: string;
  menuPosition?: number;
  isArchived?: boolean;
  createdTime?: { seconds: number };
}

export interface MenuSection {
  id: string;
  name: string;
  items: Item[];
}

export interface Roaster {
  name: string;
  location?: string;
  blurb?: string;
  logo?: string;
  /** This roaster's bags on the shelf, named without the roaster ("La Joya"). */
  bags: Item[];
  /** "On the bar since late September", from when its first bag went into the catalog. */
  since?: string;
}

export interface CoffeeBoard {
  sections: MenuSection[];
  roaster?: Roaster;
  /** The pastry case: the catalog's Pastries items that have a photo, in menu order. Not on the boards. */
  pastries: Item[];
}

/** The section's items, the way the board picks them: by subcategory, else by hand-picked id. */
const itemsFor = (section: BoardSection, items: Item[]) => {
  const subcategories = section.productSubcategoryIds?.length
    ? section.productSubcategoryIds
    : section.productSubcategoryId
    ? [section.productSubcategoryId]
    : [];
  const picked = subcategories.length
    ? items.filter((item) => subcategories.includes(item.productSubcategoryId ?? ''))
    : (section.productIds ?? []).map((id) => items.find((item) => item.id === id)).filter((item): item is Item => !!item);
  return picked
    .filter((item) => !item.isArchived && !section.productIdsToHide?.includes(item.id))
    .sort((a, b) => (a.menuPosition ?? 0) - (b.menuPosition ?? 0));
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const sinceLabel = (bags: Item[]) => {
  const first = Math.min(...bags.map((bag) => bag.createdTime?.seconds ?? Infinity));
  if (!isFinite(first)) {
    return undefined;
  }
  const date = new Date(first * 1000);
  const part = date.getDate() <= 10 ? 'early' : date.getDate() <= 20 ? 'mid' : 'late';
  return `On the bar since ${part} ${MONTHS[date.getMonth()]}`;
};

const roasterFrom = (section: BoardSection, items: Item[]): Roaster => {
  const all = itemsFor(section, items);
  // The shelf can carry a guest decaf from another roaster; keep to this roaster's bags when they're named
  const key = (section.roasterName ?? '').split(' ')[0].toLowerCase();
  const own = key ? all.filter((item) => item.name.toLowerCase().includes(key)) : [];
  const bags = (own.length ? own : all).map((item) => ({
    ...item,
    name: item.name.split(/\s+-\s+/)[0].trim(),
  }));
  return {
    name: section.roasterName ?? '',
    location: section.roasterLocation,
    blurb: section.roasterBlurb,
    logo: section.roasterLogo,
    bags,
    since: sinceLabel(own.length ? own : all),
  };
};

/**
 * The boards' list sections and featured roaster. Whole-bean sections are left
 * to the roaster, which already shows the bags. Undefined while loading, null
 * if Project 9 couldn't be reached.
 */
export const useCoffeeBoard = () => {
  const [categories, setCategories] = React.useState<Category[]>();
  const [menus, setMenus] = React.useState<CustomMenu[]>();
  const [items, setItems] = React.useState<Item[]>();
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    const db = project9Db();
    const fail = (error: Error) => {
      console.error('Could not load the coffee menu from Project 9:', error);
      setFailed(true);
    };
    const unsubscribers = [
      onSnapshot(
        collection(db, 'productCategories'),
        (snapshot) => setCategories(snapshot.docs.map((doc) => ({ ...(doc.data() as Category), id: doc.id }))),
        fail
      ),
      onSnapshot(
        collection(db, 'customMenus'),
        (snapshot) => setMenus(snapshot.docs.map((doc) => ({ ...(doc.data() as CustomMenu), id: doc.id }))),
        fail
      ),
    ];
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
  }, []);

  const coffeeCategory = categories?.find((category) => /coffee/i.test(category.name));
  const coffeeCategoryId = coffeeCategory?.id;

  React.useEffect(() => {
    if (!coffeeCategoryId) {
      return;
    }
    return onSnapshot(
      query(collection(project9Db(), 'productBaseItem'), where('productCategoryId', '==', coffeeCategoryId)),
      (snapshot) => setItems(snapshot.docs.map((doc) => ({ ...(doc.data() as Item), id: doc.id }))),
      (error) => {
        console.error('Could not load the coffee menu items from Project 9:', error);
        setFailed(true);
      }
    );
  }, [coffeeCategoryId]);

  return React.useMemo((): CoffeeBoard | null | undefined => {
    if (failed) {
      return null;
    }
    if (!categories || !menus || !items) {
      return undefined;
    }
    const boards = menus
      .filter((menu) => menu.productCategoryId === coffeeCategoryId)
      .sort((a, b) => a.name.localeCompare(b.name));
    const sections: MenuSection[] = [];
    let roaster: Roaster | undefined;
    for (const board of boards) {
      for (const column of board.columns ?? []) {
        for (const section of column.sections ?? []) {
          if (section.sectionType === 'featuredRoaster') {
            roaster = roaster ?? roasterFrom(section, items);
          } else if (!/bean/i.test(section.name)) {
            const sectionItems = itemsFor(section, items);
            if (sectionItems.length) {
              sections.push({ id: section.id, name: section.name, items: sectionItems });
            }
          }
        }
      }
    }
    const pastryIds = (coffeeCategory?.subcategories ?? [])
      .filter((subcategory) => /pastr/i.test(subcategory.name))
      .map((subcategory) => subcategory.id);
    const pastries = items
      .filter((item) => pastryIds.includes(item.productSubcategoryId ?? '') && !item.isArchived && !!item.images?.[0])
      .sort((a, b) => (a.menuPosition ?? 0) - (b.menuPosition ?? 0));
    return { sections, roaster, pastries };
  }, [failed, categories, menus, items, coffeeCategory, coffeeCategoryId]);
};
